import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    signal,
    Signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {AuthService, LocalConfigurationService} from "@fedelta-pos/data-client";
import {SessionModel} from "../../session/session.model";
import {Local} from "../../configuration/config";
import {ButtonStyle} from "../../configuration/app-entity.model";
import {navParam} from "../../navigation/navigation-params";
import {Page, PageAlignment} from "../../navigation/page";
import {IdentificationMode} from '../../identity/identification-page/identification-mode';
import {IdentityModel, LoginStatus} from '../../identity/identity.model';
import {firstValueFrom} from "rxjs";
import {LoadingStatus} from '../../session/loading-status';
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonConnotation, ButtonProminence} from "../../buttons/button/button";
import {IconButtonComponent} from "../../buttons/icon-button/icon-button.component";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {LinkComponent} from "../../buttons/link/link.component";
import {AppDeviceService} from '../../app-device/app-device.service';
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {LogoComponent} from "../../ui-elements/logo/logo.component";
import {FormComponent} from '../../inputs/form/form.component';
import {TextFieldComponent} from '../../inputs/text-field/text-field.component';
import {TranslateModule} from '@ngx-translate/core';
import {FullPageLayoutComponent} from '../../page-layout/full-page-layout/full-page-layout.component';
import {FormsModule} from '@angular/forms';
import {HtmlPipe} from '../../utils/html.pipe';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {onEach} from '../../utils/observable-utils';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {TrimPipe} from '../../utils/trim.pipe';
import {AutomaticLoginIdentity, CustomerDetailsService} from '../../customer-info/customer-details.service';

@Component({
    selector: "woa-entry-page",
    templateUrl: "./entry-page.component.html",
    styleUrl: "./entry-page.component.scss",
    providers: [{provide: Page, useExisting: EntryPageComponent}],
    imports: [
        ButtonComponent,
        ContentMessageComponent,
        FormComponent,
        FormsModule,
        FullPageLayoutComponent,
        HtmlPipe,
        IconButtonComponent,
        LinkComponent,
        LogoComponent,
        PageFooterControlDirective,
        TextFieldComponent,
        TranslateModule,
        TrimPipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntryPageComponent extends Page implements OnInit {
    public sessionModel: SessionModel = inject(SessionModel);
    public appDeviceService: AppDeviceService = inject(AppDeviceService);
    public identityModel: IdentityModel = inject(IdentityModel);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private customerDetailsService: CustomerDetailsService = inject(CustomerDetailsService);
    private loadingService: LoadingService = inject(LoadingService);
    private authService: AuthService = inject(AuthService);
    private localConfigurationService: LocalConfigurationService = inject(LocalConfigurationService);
    private destroyRef: DestroyRef = inject(DestroyRef);

    ButtonProminence = ButtonProminence;
    ButtonConnotation = ButtonConnotation;
    ButtonStyle = ButtonStyle;
    IdentificationMode = IdentificationMode;
    IconButtonType = IconButtonType;
    MessageLevel = MessageLevel;
    PageAlignment = PageAlignment;

    public readonly form: Signal<FormComponent> = viewChild.required(FormComponent);

    private readonly suppressReset: Signal<boolean> = navParam<boolean>("suppressReset", true);

    public initialised: WritableSignal<boolean> = signal(undefined);
    public allowActions: WritableSignal<boolean> = signal(false);
    public urlDisplayed: WritableSignal<boolean> = signal(false); // This is only enabled for demo purposes to allow the server to be manually specified.
    public demoMode: WritableSignal<boolean> = signal(false); // Indicates if the app is set up in demo mode
    public serverURL: WritableSignal<string> = signal(undefined); // Server URL to override in the local/dataserverURL config
    public waiterName: WritableSignal<string> = signal(undefined);

    public readonly showRetryButton: Signal<boolean> = computed<boolean>(() => !this.urlDisplayed() && this.initialised() == false);
    public readonly showExitButton: Signal<boolean> = computed<boolean>(() => this.showRetryButton() && this.appDeviceService.isAvailable());
    public readonly showActionButtons: Signal<boolean> = computed<boolean>(() => !this.urlDisplayed() && this.initialised() && this.allowActions());
    public readonly showWaiterNameEntry: Signal<boolean> = computed<boolean>(() => this.showActionButtons() && this.configurationModel.requireWaiterName());
    public readonly showStartAsGuestButton: Signal<boolean> = computed<boolean>(() => this.showActionButtons() && !this.configurationModel.allowMemberLogin() && !this.showWaiterNameEntry());
    public readonly showLoginButtons: Signal<boolean> = computed<boolean>(() => this.showActionButtons() && this.configurationModel.allowMemberLogin());
    public readonly showRegisterButton: Signal<boolean> = computed<boolean>(() => this.showLoginButtons() && this.configurationModel.supportsRegistration());
    public readonly messageLine1: Signal<string> = computed(() => this.appDeviceService.deviceRegistrationRequired() ? "woa.message.entry.register-device.line1" : this.showWaiterNameEntry() ? "woa.message.entry.identify-waiter.line1" : "woa.message.entry.welcome.line1");
    public readonly messageLine2: Signal<string> = computed(() => this.appDeviceService.deviceRegistrationRequired() ? "woa.message.entry.register-device.line2" : this.showWaiterNameEntry() ? "woa.message.entry.identify-waiter.line2" : "woa.message.entry.welcome.line2");
    public readonly messageLine3: Signal<string> = computed(() => this.appDeviceService.deviceRegistrationRequired() ? "woa.message.entry.register-device.line3" : this.showWaiterNameEntry() ? "woa.message.entry.identify-waiter.line3" : "woa.message.entry.welcome.line3");
    public readonly deviceNotPresent: Signal<boolean> = computed<boolean>(() => this.configurationModel.production() && this.appDeviceService.isDevice() && !this.appDeviceService.isAvailable());

    constructor() {
        super();
        (async () => this.demoMode.set(await this.localConfigurationService.getBoolean(Local.DEMO)))();
    }

    ngOnInit(): void {
        void this.initialise();

        onEach(this.authService.loginRevoked$, {
            next: async () => {
                if (this.initialised()) {
                    return this.startAsGuest();
                }
            },
            error: async (error: any) => {
                console.error(error);
                return this.startAsGuest();
            }
        }, this.destroyRef);
    }

    private async initialise(): Promise<void> {
        if (!this.suppressReset()) {
            this.sessionModel.clear("initialise entry page");
        }
        this.identityModel.waiterName.set(undefined);
        this.initialised.set(undefined);
        this.allowActions.set(false);
        try {
            await this.initialLogin();

            if (this.appDeviceService.isAvailable()) {
                try {
                    // If this fails (typically because the server is unavailable), appDeviceService.deviceRegistrationRequired will
                    // return false, because it can't determine anything about the device registration.
                    // That will cause the normal error state to be displayed.
                    await this.appDeviceService.refreshAppDevice("entry page");
                } catch (ignore) {
                }
            }

            if (this.appDeviceService.deviceRegistrationRequired()) {
                console.log("App Device is not registered");
                await this.configurationModel.loadApp();
                this.initialised.set(false);
                this.sessionModel.notifyInitialLoadStatus(LoadingStatus.FAILED);
            } else if (this.deviceNotPresent()) {
                console.log("App Device is not present");
                this.initialised.set(false);
                this.sessionModel.notifyInitialLoadStatus(LoadingStatus.FAILED);
            } else {
                await this.configurationModel.loadApp();
                this.initialised.set(true);

                void this.sessionModel.notifyAppUsage(true);

                this.allowActions.set(true);

                if (this.configurationModel.supportsAutomaticLogin() && this.identityModel.isGuestUser()) {
                    const automaticLoginIdentity: AutomaticLoginIdentity = this.customerDetailsService.getAutomaticLoginIdentity();
                    if (automaticLoginIdentity) {
                        await this.identityModel.loginAsMember(this.configurationModel.loginType(), true, automaticLoginIdentity.identity, automaticLoginIdentity.authKey);
                    }
                }

                if (this.showWaiterNameEntry()) {
                    this.sessionModel.notifyInitialLoadStatus(LoadingStatus.LOADED);
                } else {
                    await this.sessionModel.goToLandingPage(this.suppressReset());
                }
            }
        } catch (error) {
            console.error("Failed to initialise app", error);
            this.initialised.set(false);
            this.sessionModel.notifyInitialLoadStatus(LoadingStatus.FAILED);
        }
    }

    private async initialLogin(): Promise<void> {
        if (!this.authService.isLoggedIn()) {
            await this.identityModel.loginAsGuest();
            return;
        }
        try {
            if (!await firstValueFrom(this.authService.refreshAccessToken())) {
                await this.identityModel.loginAsGuest();
            }
        } catch (error) {
            await this.identityModel.loginAsGuest();
        }
    }

    public retryInitialisation(): void {
        this.sessionModel.notifyInitialLoadStatus(LoadingStatus.RELOADING);
        void this.loadingService.performWithSpinner(() => this.initialise());
    }

    public async identify(mode: IdentificationMode): Promise<void> {
        if (await this.navigationService.createPage("woa-identification-page", {"mode": mode})) {
            void this.sessionModel.goToLandingPage();
        }
    }

    public async startAsGuest(): Promise<void> {
        if ((await this.identityModel.loginAsGuest()).status === LoginStatus.SUCCESS) {
            void this.sessionModel.goToLandingPage(this.suppressReset());
        }
    }

    public async confirmWaiterName(): Promise<void> {
        if (!this.form().valid) {
            return;
        }
        this.identityModel.waiterName.set(this.waiterName());
        void this.startAsGuest();
    }

    /**
     * Displays panel to set the server URL. Only when in demo mode
     */
    public async openChangeURL(): Promise<void> {
        if (!this.urlDisplayed()) {
            this.serverURL.set(await this.localConfigurationService.getString(Local.DATASERVER_URL));
            this.urlDisplayed.set(true);
        }
    }

    /**
     * Closes the URL panel
     */
    public cancelURL(): void {
        this.urlDisplayed.set(false);
    }


    /**
     * Overrides the local/dataserverURL config
     */
    public saveURL(): void {
        this.localConfigurationService.saveString(Local.DATASERVER_URL, this.serverURL());
        this.urlDisplayed.set(false);
        void this.initialise();
    }
}
